/**
 * Route Permissions Configuration
 * Maps routes to their required job permissions
 * Frontend-controlled - no backend dependency
 */

export interface RoutePermission {
  route: string;
  job: string;
}

// Route to Job mapping - defines which job permission is required for each route
export const ROUTE_PERMISSIONS: RoutePermission[] = [
  // Dashboard - no specific permission required (authenticated users can access)
  { route: '/dashboard', job: '' },

  // ============== Core Module ==============
  // Monitoring
  { route: '/error-logs', job: 'errorlogs' },
  { route: '/background-jobs', job: 'backgroundjob' },
  { route: '/log-transaction', job: 'logtransaction' },
  { route: '/prompt-executions', job: 'promptexecutions' },
  // Gated on the AI monitor's own job rather than a new one: the page is a
  // rollup of prompt_execution, rag_query and eval_run, and minting a
  // permission would mean a backend migration, a seed and a matching edit to
  // the hand-written mirror in src/constants/permissions.ts.
  { route: '/ai-observability', job: 'promptexecutions' },
  { route: '/ai-observability/prompts', job: 'promptexecutions' },
  { route: '/ai-observability/executions', job: 'promptexecutions' },
  { route: '/ai-observability/traces', job: 'promptexecutions' },
  { route: '/ai-observability/tracing', job: 'promptexecutions' },
  { route: '/ai-observability/master', job: 'promptexecutions' },

  // Masters
  { route: '/units', job: 'units' },
  { route: '/personnel', job: 'personnel' },
  { route: '/unit-villages', job: 'unitvillages' },
  { route: '/districts', job: 'district' },
  { route: '/departments', job: 'departments' },
  { route: '/ministries', job: 'ministries' },
  { route: '/projects', job: 'apps' },
  { route: '/app-config', job: 'appconfig' },
  { route: '/unit-types', job: 'unittype' },
  { route: '/error-master', job: 'errormaster' },
  { route: '/feedback-master', job: 'feedbackmaster' },
  { route: '/log-master', job: 'logmaster' },
  { route: '/designation-master', job: 'designationmaster' },
  { route: '/prompt-table', job: 'prompttable' },
  { route: '/ai-models', job: 'aimodelmaster' },
  // Document Intelligence: the analyze-document endpoint its pages call is
  // gated on AI Models READ, so the route is too until it has a job of its own.
  { route: '/document-intelligence', job: 'aimodelmaster' },
  { route: '/agents', job: 'agent' },
  { route: '/conversations', job: 'conversations' },
  { route: '/chat', job: 'conversations' },
  { route: '/eval-cases', job: 'evalcase' },
  { route: '/functions', job: 'functionmaster' },

  // ============== Notification Engine ==============
  { route: '/notification-types', job: 'notificationtype' },
  { route: '/notification-masters', job: 'notificationmaster' },
  { route: '/recipient-masters', job: 'recipientmaster' },
  { route: '/recipient-lists', job: 'recipientlist' },
  { route: '/action-masters', job: 'actionmaster' },
  { route: '/schedules', job: 'schedulemaster' },
  { route: '/notification-runs', job: 'notificationruns' },
  { route: '/evals', job: 'evals' },
  { route: '/eval-runs', job: 'evalrun' },
  { route: '/knowledge-bases', job: 'knowledge_base' },
  { route: '/pipelines', job: 'pipeline' },
  { route: '/pipeline-runs', job: 'pipelinerun' },
  { route: '/rags', job: 'rag' },

  // Configurations
  { route: '/value-sets', job: 'valuesets' },

  // Other Core
  { route: '/file-uploads', job: 'fileupload' },

  // Auth & RBAC — the Access Control admin area (maps to the ROLES job → AUTH_ROLE.READ)
  { route: '/access-control', job: 'roles' },
  // User management (maps to the USER_MANAGEMENT job → USER.READ); the page also has
  // an in-page PermissionGuard, this closes the route-layer fail-open.
  { route: '/user-management', job: 'usermanagement' },

  // ============== 91CrPC Module ==============
  { route: '/crpc-requestss', job: 'crpcrequests' },
  { route: '/crpc-request-pipeliness', job: 'crpcrequestpipelines' },
  { route: '/upload', job: 'datareceived' },
  { route: '/consolidate-data', job: 'consolidatedrequests' },
  { route: '/service-master', job: 'servicemaster' },
  { route: '/operator-list-master', job: 'operatorslist' },
  { route: '/operator-service-mapping-master', job: 'operatorsservicelist' },
  { route: '/data-received-filess', job: 'datareceivedfiles' },
  { route: '/crpc-request-generators', job: 'crpcrequestgenerator' },
  { route: '/pending-approvals', job: 'mypendingapprovals' },
  { route: '/my-requests', job: 'myrequest' },
  { route: '/service-management', job: 'servicemanagement' },

  // ============== CDR IPDR Module ==============
  { route: '/investigation-documents', job: 'investigationdocument' },
  { route: '/investigation-task-executions', job: 'investigationtaskexecution' },
  { route: '/investigations', job: 'investigation' },
  { route: '/investigation-document-types', job: 'investigationdocumenttype' },
  { route: '/investigation-tasks', job: 'investigationtask' },
];

/**
 * Get the required job for a route path
 * Matches the base route (ignores /create, /:id, /edit suffixes)
 * Handles /core prefix when accessed via main_fe
 */
export const getJobForRoute = (pathname: string): string | null => {
  // Normalize path - get base route
  const segments = pathname.split('/').filter(Boolean);

  // If first segment is 'core' (MFE prefix), use second segment
  // e.g., /core/personnel/:id -> personnel
  const baseSegment = segments[0] === 'core' && segments.length > 1
    ? segments[1]
    : segments[0];

  const basePath = '/' + baseSegment;

  const permission = ROUTE_PERMISSIONS.find(p => p.route === basePath);
  return permission?.job ?? null;
};

/**
 * Check if a job exists in user's permissions
 * First checks currentModuleJobs, then falls back to full permissions array for Core module
 */
export const hasJobPermission = (
  jobName: string,
  permissionsData: any
): boolean => {
  if (!jobName) return true; // Empty job = no permission required

  const normalizedJobName = jobName.toLowerCase().replace(/[-_\s]/g, '');

  // First, check currentModuleJobs if it's for Core module
  if (
    permissionsData?.currentModuleJobs?.moduleName?.toLowerCase() === 'core' &&
    permissionsData?.currentModuleJobs?.jobs?.length > 0
  ) {
    return permissionsData.currentModuleJobs.jobs.some((job: any) => {
      const normalized = job.jobName.toLowerCase().replace(/[-_\s]/g, '');
      return normalized === normalizedJobName;
    });
  }

  // Fallback: Check the full permissions array for Core module
  // This handles the case when navigating from another module and currentModuleJobs hasn't updated yet
  if (permissionsData?.permissions?.length > 0) {
    const coreModule = permissionsData.permissions.find(
      (module: any) => module.moduleName?.toLowerCase() === 'core'
    );
    if (coreModule?.jobs?.length > 0) {
      return coreModule.jobs.some((job: any) => {
        const normalized = job.jobName.toLowerCase().replace(/[-_\s]/g, '');
        return normalized === normalizedJobName;
      });
    }
  }

  return false;
};
